import { memo, ReactNode, useState, ImgHTMLAttributes } from 'react';
import cn from 'classnames';
import { ImageType } from '@/shared/types/common';
import defaultFallbackImage from '@/shared/assets/images/image-placeholder.jpg';
import styles from './AppImage.module.scss';

export interface AppImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'alt' | 'src'> {
  alt: string;
  src?: ImageType;
  className?: string;
  fallback?: ReactNode;
  fallbackImage?: ImageType;
  onLoad?: () => void;
  lazy?: boolean;
  width?: number | string;
  height?: number | string;
}

export const AppImage = memo(
  ({
    className,
    alt,
    src,
    fallback,
    width = 0,
    height = 0,
    onError,
    lazy,
    fallbackImage,
    ...imageProps
  }: AppImageProps) => {
    const [error, setError] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const getImageSrc = (img: ImageType | undefined): string | undefined => {
      if (!img) return undefined;
      if (typeof img === 'string') return img;
      return img.src;
    };

    const imageSrc = getImageSrc(src);
    const fallbackSrc = getImageSrc(fallbackImage) || defaultFallbackImage;

    if (!imageSrc || error) {
      if (!fallback) {
        return (
          <img
            src={fallbackSrc}
            alt={alt}
            width={width}
            height={height}
            className={className}
            {...(imageProps as ImgHTMLAttributes<HTMLImageElement>)}
          />
        );
      }
      return <div className={className}>{fallback}</div>;
    }

    if (lazy) {
      return (
        <div className={cn(styles.root, { [styles.loaded]: loaded }, className)}>
          <img
            src={imageSrc}
            alt={alt}
            width={width}
            height={height}
            className={cn(styles.image, className)}
            loading="lazy"
            onError={(event) => {
              setError(true);
              onError?.(event);
            }}
            onLoad={() => {
              setLoaded(true);
            }}
            {...(imageProps as ImgHTMLAttributes<HTMLImageElement>)}
          />
        </div>
      );
    }

    return (
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        onError={(event) => {
          setError(true);
          onError?.(event);
        }}
        onLoad={() => {
          setLoaded(true);
        }}
        {...(imageProps as ImgHTMLAttributes<HTMLImageElement>)}
      />
    );
  },
);
